import { useEffect, useRef, useState } from 'react';
import { Checkbox, Container, Form, FormField, Group, PhoneField, SelectNew, Text, TextField, useMelioForm } from '@melio/penny';

import { useApp, Button, Banner, Card, NakedButton } from '@/components/ds';
import { IndustryField, AddressFields } from '@/components/form';
import { PageTitle } from '@/containers/dashboard';
import { createEntityWithAuth } from '@/api/payouts';
import { getSonarSessionToken } from '@/lib/sonarSdk';
import {
  EMAIL_RE,
  emptyAddress,
  businessTypeOptions,
  taxTypeOptions,
  defaultTaxTypeForBusinessType,
  buildEntityBody,
} from '@/lib/entityForm';
import type { Go } from '@/lib/types';

// dashboard-add-entity.tsx — "Add entity" in the dashboard's Entities section: creates another KYB
// business entity on the signed-in partner, in the currently selected env. Collects the same fields
// the onboarding wizard collects for the partner's first entity (containers/onboarding.tsx) — owner
// (who also doubles as the entity's point of contact — see buildEntityBody), business profile,
// legal/tax, addresses — as a single page rather than a multi-step wizard (no beta application here;
// the partner already exists), and submits the same `buildEntityBody` shape via the signed-in user's
// access token (never a partner api-key), minting a Sonar token the same way `ensureEntityForEnv`
// does for the first entity.

interface FieldSpec {
  name: string;
  label: string;
  test?: (v: any) => boolean;
  message?: string;
}

const ownerSpecs: FieldSpec[] = [
  { name: 'ownerFirst', label: 'First name' },
  { name: 'ownerLast', label: 'Last name' },
  { name: 'ownerEmail', label: 'Owner email', test: (v) => EMAIL_RE.test(String(v ?? '').trim()), message: 'Enter a valid email' },
  { name: 'ownerDob', label: 'Date of birth', test: (v) => v instanceof Date && !isNaN(v as unknown as number) },
  { name: 'ownerPhone', label: 'Phone number' },
];
const businessInfoSpecs: FieldSpec[] = [
  { name: 'legalName', label: 'Business name' },
  { name: 'industry', label: 'Industry', test: (v) => !!(v && v.naicsCode), message: 'Select an industry' },
  { name: 'phone', label: 'Business phone' },
  { name: 'website', label: 'Business website' },
];
const legalInfoSpecs: FieldSpec[] = [{ name: 'taxId', label: 'Tax ID' }];
const legalAddressSpecs: FieldSpec[] = [
  { name: 'legalAddress.line1', label: 'Address line 1' },
  { name: 'legalAddress.city', label: 'City' },
  { name: 'legalAddress.state', label: 'State' },
  { name: 'legalAddress.postalCode', label: 'Postal code' },
];
const addressSpecs: FieldSpec[] = [
  { name: 'address.line1', label: 'Address line 1' },
  { name: 'address.city', label: 'City' },
  { name: 'address.state', label: 'State' },
  { name: 'address.postalCode', label: 'Postal code' },
];

// A titled section card — the page's field groups (owner, business, legal, addresses).
function Section({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <Card padding={24} style={{ marginBottom: 16 }}>
      <Container paddingBottom="s">
        <Text as="h2" textStyle="heading4Semi">
          {title}
        </Text>
        {sub && (
          <Text textStyle="body4" color="semantic.text.secondary">
            {sub}
          </Text>
        )}
      </Container>
      {children}
    </Card>
  );
}

interface AddEntityViewProps {
  go: Go;
}

function AddEntityView({ go }: AddEntityViewProps) {
  const app = useApp();
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState('');

  const form: any = useMelioForm({
    defaultValues: {
      ownerFirst: '',
      ownerLast: '',
      ownerEmail: '',
      ownerPhone: '',
      ownerDob: null,
      legalName: '',
      industry: undefined,
      phone: '',
      website: '',
      businessType: 'llc',
      taxIdType: 'ein',
      taxId: '',
      address: { ...emptyAddress },
      legalAddress: { ...emptyAddress },
      operatingSameAsLegal: true,
    },
    onSubmit: () => void createEntityNow(),
  });
  const { formProps, registerField, watch, getValues, setValue, setError, clearErrors } = form;

  const taxIdType = watch('taxIdType');
  const businessType = watch('businessType');
  const operatingSameAsLegal = watch('operatingSameAsLegal');

  useEffect(() => {
    setValue('taxIdType', defaultTaxTypeForBusinessType(businessType));
  }, [businessType]);

  const checkRequired = (specs: FieldSpec[]) => {
    specs.forEach((s) => clearErrors(s.name));
    const v = getValues();
    const get = (path: string) => path.split('.').reduce<any>((o, k) => (o == null ? o : o[k]), v);
    let firstMsg = '';
    for (const s of specs) {
      const val = get(s.name);
      const ok = s.test ? s.test(val) : !(val == null || val === '');
      if (!ok) {
        setError(s.name, { type: 'required', message: s.message || 'Required' });
        if (!firstMsg) firstMsg = `Please complete: ${s.label}`;
      }
    }
    return firstMsg;
  };

  // Re-entrancy guard, same reasoning as the onboarding wizard's submit: a ref (not state) so this
  // closure never reads a stale value.
  const submittingRef = useRef(false);
  const createEntityNow = async () => {
    if (submittingRef.current) return;
    const specs = [
      ...ownerSpecs,
      ...businessInfoSpecs,
      ...legalInfoSpecs,
      ...legalAddressSpecs,
      ...(operatingSameAsLegal ? [] : addressSpecs),
    ];
    const err = checkRequired(specs);
    if (err) {
      setSubmitErr(err);
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitErr('');
    try {
      const entityBody = buildEntityBody(getValues());
      if (!app.partnerName) throw new Error('No partner found for this account.');
      const sonarToken = await getSonarSessionToken(app.env, app.partnerName);
      // authRequest resolves the token itself (refreshing + retrying once on a 401, and starting the
      // sign-in flow if the session is really gone), so no token is threaded through here.
      await createEntityWithAuth({ env: app.env, getToken: app.getAccessToken }, entityBody, { sonarToken });
      go('/dashboard/entities');
    } catch (ex: any) {
      setSubmitErr(ex?.message || 'We could not create this entity. Please try again.');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageTitle
        title="Add entity"
        sub="Create another legal entity to onboard and pay from."
        action={
          <NakedButton leftIcon="arrow-left" color="var(--text-secondary)" onClick={() => go('/dashboard/entities')}>
            All entities
          </NakedButton>
        }
      />

      <Section title="Business owner" sub="The primary individual who owns or controls the business — also this entity's point of contact.">
        <Form {...formProps} columns={2}>
          <FormField
            isRequired
            {...registerField('ownerFirst')}
            labelProps={{ label: 'First name *' }}
            render={(props: any) => <TextField {...props} placeholder="Jane" />}
          />
          <FormField
            isRequired
            {...registerField('ownerLast')}
            labelProps={{ label: 'Last name *' }}
            render={(props: any) => <TextField {...props} placeholder="Doe" />}
          />
          <FormField
            colSpan={2}
            isRequired
            {...registerField('ownerEmail')}
            labelProps={{ label: 'Email *' }}
            render={(props: any) => <TextField {...props} type="email" placeholder="jane.doe@sunrisebakery.co" />}
          />
          <Form.DateField
            {...registerField('ownerDob')}
            colSpan={2}
            isRequired
            labelProps={{ label: 'Date of birth' }}
            isTypable
            format="MM/dd/yyyy"
            placeholder="MM/DD/YYYY"
            weekDays={[0, 1, 2, 3, 4, 5, 6]}
          />
          <FormField
            colSpan={2}
            isRequired
            {...registerField('ownerPhone')}
            labelProps={{ label: 'Phone number *' }}
            render={(props: any) => <PhoneField {...props} placeholder="(###) ###-####" />}
          />
        </Form>
      </Section>

      <Section title="Business profile">
        <Form {...formProps} columns={2}>
          <FormField
            colSpan={2}
            isRequired
            {...registerField('legalName')}
            labelProps={{ label: 'Business name *' }}
            helperText="How your business appears in Melio"
            render={(props: any) => <TextField {...props} placeholder="Sunrise Bakery LLC" />}
          />
          <IndustryField
            form={form}
            env={app.env}
            name="industry"
            label="Business industry"
            isRequired
            colSpan={2}
            getToken={app.getAccessToken}
          />
          <FormField
            colSpan={2}
            isRequired
            {...registerField('phone')}
            labelProps={{ label: 'Business phone number *' }}
            render={(props: any) => <PhoneField {...props} placeholder="(###) ###-####" />}
          />
          <FormField
            colSpan={2}
            isRequired
            {...registerField('website')}
            labelProps={{ label: 'Business website *' }}
            helperText="Shown to your customers in emails from us."
            render={(props: any) => <TextField {...props} placeholder="https://sunrisebakery.co" />}
          />
        </Form>
      </Section>

      <Section title="Business type & tax">
        <Form {...formProps} columns={2}>
          <FormField
            colSpan={2}
            isRequired
            {...registerField('businessType')}
            labelProps={{ label: 'Business type *' }}
            render={(props: any) => (
              <SelectNew
                {...props}
                options={businessTypeOptions}
                optionRenderer={(option: any) => (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '4px 0' }}>
                    <Text textStyle="body3Semi">{option.label}</Text>
                    {option.description && (
                      <Text textStyle="body4" color="global.neutral.900">
                        {option.description}
                      </Text>
                    )}
                  </div>
                )}
                placeholder="Select a type"
              />
            )}
          />
          <FormField
            isRequired
            {...registerField('taxIdType')}
            labelProps={{ label: 'Tax ID type *' }}
            render={(props: any) => <SelectNew {...props} options={taxTypeOptions} placeholder="Select" />}
          />
          <FormField
            isRequired
            {...registerField('taxId')}
            labelProps={{ label: `${taxIdType === 'ein' ? 'EIN' : String(taxIdType || '').toUpperCase()} *` }}
            render={(props: any) => <TextField {...props} placeholder={taxIdType === 'ein' ? '84-3920481' : '123-45-6789'} />}
          />
        </Form>
      </Section>

      <Section title="Legal registration address">
        <Form {...formProps} columns={2}>
          <AddressFields form={form} env={app.env} parentFieldName="legalAddress" label="Legal address *" getToken={app.getAccessToken} />
        </Form>
      </Section>

      <Section title="Operating address">
        <Form {...formProps} columns={2}>
          <FormField
            colSpan={2}
            labelProps={{ label: 'Operating address', isHidden: true }}
            render={() => (
              <Checkbox
                value={operatingSameAsLegal}
                onChange={(checked: boolean) => setValue('operatingSameAsLegal', checked)}
                label="Same as legal registration address"
              />
            )}
          />
          {!operatingSameAsLegal && (
            <AddressFields form={form} env={app.env} parentFieldName="address" label="Operating address *" getToken={app.getAccessToken} />
          )}
        </Form>
      </Section>

      {submitErr && (
        <Banner kind="critical" style={{ marginTop: 8, marginBottom: 16 }}>
          {submitErr}
        </Banner>
      )}
      <Group spacing="s">
        <Button variant="tertiary" size="large" disabled={submitting} onClick={() => go('/dashboard/entities')}>
          Cancel
        </Button>
        <Button size="large" loading={submitting} onClick={createEntityNow}>
          Create entity
        </Button>
      </Group>
    </div>
  );
}

export { AddEntityView };
